import React, { FC, useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Area,
  DecodedPhoto,
  FRAME,
  FRAME_ASPECT,
  checkCropQuality,
  isPhotoTooSmall,
  previewBackground,
} from '../../../lib/contactPhoto';
import { PhoneButtonPreview } from './PhoneButtonPreview';
import { useContactPhotoText } from './contactPhotoText';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.1;

/**
 * failed     — no answer from the server (offline). Retry may help.
 * refused    — the server answered no. Retry will not help: contact support.
 * unprepared — this device could not produce the crop. Choose another photo.
 */
export type SaveStatus = 'idle' | 'saving' | 'failed' | 'refused' | 'unprepared';

const SAVE_MESSAGE = {
  failed: 'uploadFailed',
  refused: 'uploadRefused',
  unprepared: 'cannotOpen',
} as const;

interface PhotoCropStepProps {
  photo: DecodedPhoto;
  contactName: string;
  status: SaveStatus;
  onConfirm: (areaPixels: Area) => void;
  onCancel: () => void;
  onChooseAnother: () => void;
}

/**
 * Face guide drawn over the crop frame. An oval sized so that, when the face
 * fills it, the face fills almost the whole phone button — the same size for
 * every contact. Purely a guide; it never receives pointer events.
 */
const FaceOutline: FC<{ width: number; height: number }> = ({ width, height }) => (
  <svg
    data-testid="face-outline"
    aria-hidden="true"
    width={width}
    height={height}
    viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
  >
    <ellipse cx="92" cy="76" rx="50" ry="66" fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="4" />
    <ellipse cx="92" cy="76" rx="50" ry="66" fill="none" stroke="#ffffff" strokeWidth="2" strokeDasharray="6 5" />
  </svg>
);

export const PhotoCropStep: FC<PhotoCropStepProps> = ({
  photo,
  contactName,
  status,
  onConfirm,
  onCancel,
  onChooseAnother,
}) => {
  const text = useContactPhotoText();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPercent, setAreaPercent] = useState<Area | null>(null);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [cropSize, setCropSize] = useState<{ width: number; height: number } | null>(null);

  const onAreaChange = useCallback((percent: Area, pixels: Area) => {
    setAreaPercent(percent);
    setAreaPixels(pixels);
  }, []);

  // Said at once, not on submit: a too-small photo, or a crop zoomed past sharp.
  const photoTooSmall = isPhotoTooSmall(photo.width, photo.height);
  const problem = photoTooSmall ? 'tooSmall' : areaPixels ? checkCropQuality(areaPixels, photo) : null;
  const saving = status === 'saving';
  const saveMessage = status === 'failed' || status === 'refused' || status === 'unprepared' ? SAVE_MESSAGE[status] : null;
  // Only "no answer" is worth retrying; a refusal or an unreadable photo is not.
  const offerAnother = photoTooSmall || status === 'unprepared';

  const handleConfirm = () => {
    if (!areaPixels || problem || saving) return;
    onConfirm(areaPixels);
  };

  const setZoomClamped = (z: number) => setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100)));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={text('cropTitle')}
      className="fixed inset-0 z-[70] flex flex-col bg-white dark:bg-slate-900 sm:items-center sm:justify-center sm:bg-black/60"
    >
      <div className="flex flex-col w-full h-full sm:h-auto sm:max-h-[95vh] sm:max-w-lg sm:rounded-2xl bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-4 pt-4 pb-2 shrink-0">
          <h2 className="font-display text-xl font-bold text-brand-text dark:text-white">{text('cropTitle')}</h2>
          <p className="text-base text-brand-text-light dark:text-slate-300 mt-1">{text('cropHint')}</p>
        </div>

        <div className="relative w-full flex-1 min-h-[260px] sm:flex-none sm:h-[360px] bg-neutral-900 touch-none">
          <Cropper
            image={photo.dataUrl}
            crop={crop}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            aspect={FRAME_ASPECT}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropAreaChange={onAreaChange}
            onCropComplete={onAreaChange}
            onCropSizeChange={setCropSize}
            style={{
              cropAreaStyle: {
                borderRadius: `${(16 / FRAME.width) * 100}% / ${(16 / FRAME.height) * 100}%`,
                border: '3px solid #ffffff',
              },
            }}
            mediaProps={{ alt: '' }}
          />
          {cropSize && <FaceOutline width={cropSize.width} height={cropSize.height} />}
        </div>

        <div className="px-4 pt-3 pb-4 shrink-0 flex flex-col gap-3 overflow-y-auto">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setZoomClamped(zoom - 0.25)}
              aria-label={text('zoomOut')}
              className="w-12 h-12 shrink-0 rounded-full border-2 border-brand-border flex items-center justify-center text-brand-text dark:text-white"
            >
              <span className="material-symbols-outlined">zoom_out</span>
            </button>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(e) => setZoomClamped(Number(e.target.value))}
              aria-label={text('zoom')}
              className="flex-1 h-12 accent-brand-primary"
            />
            <button
              type="button"
              onClick={() => setZoomClamped(zoom + 0.25)}
              aria-label={text('zoomIn')}
              className="w-12 h-12 shrink-0 rounded-full border-2 border-brand-border flex items-center justify-center text-brand-text dark:text-white"
            >
              <span className="material-symbols-outlined">zoom_in</span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            {areaPercent && (
              <div className="shrink-0" aria-label={text('previewLabel')}>
                <PhoneButtonPreview
                  name={contactName || text('noName')}
                  width={92}
                  alt={text('photoOf', { name: contactName })}
                  background={{ dataUrl: photo.dataUrl, ...previewBackground(areaPercent) }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-text-muted">{text('previewLabel')}</p>
              {problem && (
                <p role="alert" className="text-base text-red-700 mt-1">
                  {text(problem)}
                </p>
              )}
              {saveMessage && !problem && (
                <p role="alert" className="text-base text-red-700 mt-1">
                  {text(saveMessage)}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="flex-1 min-h-[48px] px-4 rounded-xl border-2 border-brand-border font-semibold text-brand-text-light dark:text-slate-300 disabled:opacity-50"
            >
              {text('cancel')}
            </button>
            {offerAnother ? (
              <button
                type="button"
                onClick={onChooseAnother}
                className="flex-1 min-h-[48px] px-4 rounded-xl bg-brand-primary text-white font-bold"
              >
                {text('uploadFromDevice')}
              </button>
            ) : status === 'refused' ? null : (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !areaPixels || problem !== null}
                className="flex-1 min-h-[48px] px-4 rounded-xl bg-brand-primary text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="material-symbols-outlined animate-spin" aria-hidden="true">progress_activity</span>
                    {text('saving')}
                  </>
                ) : status === 'failed' ? (
                  text('tryAgain')
                ) : (
                  text('usePhoto')
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoCropStep;
